import React from 'react'
import { Icon } from '../../atoms/Icon'
import { Button } from '../../atoms/Button'
import { FilterSelect } from '../../molecules/FilterPanel'
import {
  AnalysisFilterSectionContainer,
  AnalysisFilterBar,
  AnalysisLocationFilters,
  AnalysisFilterChip,
  AnalysisDivider,
  AnalysisLocationBadge,
  AnalysisFilterActions,
  AnalysisFilterToggleButton,
  AnalysisActiveFiltersCount,
  AnalysisAdvancedFiltersPanel,
  AnalysisAdvancedFiltersContent,
} from './AnalysisFilterSection.styles'
import type { AnalysisFilterSectionProps } from './AnalysisFilterSection.types'

export const AnalysisFilterSection = <TLocationType extends string = string>({
  locationType,
  onLocationTypeChange,
  locationOptions,
  locationLabel,
  radius,
  onRadiusChange,
  radiusOptions = [
    { value: 500, label: '500m' },
    { value: 1000, label: '1km' },
    { value: 2000, label: '2km' },
    { value: 5000, label: '5km' },
  ],
  showRadiusSelector = false,
  showFilters,
  onToggleFilters,
  activeFiltersCount,
  onClearAll,
  children,
  t,
}: AnalysisFilterSectionProps<TLocationType>) => {
  // Fallback for missing t function
  const translate = t || ((key: string) => key.split('.').pop() || key)
  return (
    <AnalysisFilterSectionContainer>
      <AnalysisFilterBar>
        <AnalysisLocationFilters>
          {/* Location Type Chips */}
          {locationOptions.map((option) => (
            <AnalysisFilterChip
              key={option.value}
              $active={locationType === option.value}
              onClick={() =>
                onLocationTypeChange(option.value as TLocationType)
              }
            >
              <Icon name={option.icon as any} size="small" />
              {option.label}
            </AnalysisFilterChip>
          ))}

          {/* Radius Selector */}
          {showRadiusSelector && (
            <>
              <AnalysisDivider />
              <FilterSelect
                value={radius?.toString() ?? ''}
                onChange={(value) => onRadiusChange?.(parseInt(value, 10))}
                options={radiusOptions.map((opt) => ({
                  value: opt.value.toString(),
                  label: opt.label,
                }))}
              />
            </>
          )}

          {/* Current Location Badge */}
          <AnalysisLocationBadge>
            <Icon name="map-pin" size="small" />
            {locationLabel}
          </AnalysisLocationBadge>
        </AnalysisLocationFilters>

        <AnalysisFilterActions>
          <AnalysisFilterToggleButton
            variant={showFilters ? 'secondary' : 'ghost'}
            size="small"
            onClick={onToggleFilters}
          >
            <Icon name={showFilters ? 'x' : 'filter'} size="small" />
            {showFilters
              ? translate('common.filters.hideFilters')
              : translate('common.filters.advancedFilters')}
            {activeFiltersCount > 0 && !showFilters && (
              <AnalysisActiveFiltersCount>
                {activeFiltersCount}
              </AnalysisActiveFiltersCount>
            )}
          </AnalysisFilterToggleButton>
          {activeFiltersCount > 0 && (
            <Button variant="ghost" size="small" onClick={onClearAll}>
              {translate('common.filters.clearAll')}
            </Button>
          )}
        </AnalysisFilterActions>
      </AnalysisFilterBar>

      {/* Advanced Filters Panel */}
      <AnalysisAdvancedFiltersPanel $isOpen={showFilters}>
        <AnalysisAdvancedFiltersContent>
          {children}
        </AnalysisAdvancedFiltersContent>
      </AnalysisAdvancedFiltersPanel>
    </AnalysisFilterSectionContainer>
  )
}
